import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { GlobalLoader } from '@/app/components/shared/GlobalLoader';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { useFetch } from '@/app/hooks';
import { notificationsGetSysNotification } from '../apis';
import { useNotificationTime } from '../hook/use-notification-time';
import { useRenderFormat } from '../hook/use-render-format';
import type { SystemNotification } from '../types';

export const SystemNotificationDetailDialog = ({
  openSysNotificationId,
  handleCloseDetail,
}: {
  openSysNotificationId: string;
  handleCloseDetail: () => void;
}) => {
  const t = useTranslations();
  const { renderHtmlContent } = useRenderFormat();
  const [currentNotification, setCurrentNotification] = useState<SystemNotification | undefined>();
  const [error, setError] = useState<string>('');
  const relativeTimeText = useNotificationTime(currentNotification?.publish_at ?? '');

  const { run: getNotificationDetails, loading: isLoading } = useFetch(
    (id: string) =>
      notificationsGetSysNotification({
        path: { id },
      }),
    {
      manual: true,
      onSuccess: ({ data, error }) => {
        if (data) {
          setCurrentNotification({
            ...data,
            action: data.action,
          });
        } else {
          setError(error.message);
          return;
        }
      },
    },
  );

  useEffect(() => {
    if (openSysNotificationId) {
      getNotificationDetails(openSysNotificationId);
    }
  }, [openSysNotificationId]);

  return (
    <Dialog
      open={!!openSysNotificationId}
      onOpenChange={(open) => {
        if (!open) {
          handleCloseDetail();
        }
      }}
    >
      <DialogContent className="px-0 md:px-0 lg:px-0">
        <DialogHeader>
          <DialogTitle className="px-8 sm:px-5 md:px-6">{renderHtmlContent(currentNotification?.title)}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[calc(70vh-80px)] space-y-4 overflow-y-scroll px-8 text-sm text-system-white64 sm:px-5 md:px-6">
          {isLoading ? (
            <GlobalLoader className="relative top-0 translate-y-0" />
          ) : error ? (
            <div className="text-system-white36 text-xs">{error}</div>
          ) : (
            <>
              {renderHtmlContent(currentNotification?.content)}
              <div className="text-system-white36 text-xs">{relativeTimeText}</div>
            </>
          )}
        </div>
        <DialogFooter className="px-8 sm:px-5 md:px-6">
          <Button variant="primary" size="dialog" onClick={handleCloseDetail}>
            {t('popup.dialog.notifications.ok')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
